"use client";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { X, Upload, Loader2 } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

interface ImageEffectCardProps extends React.HTMLAttributes<HTMLDivElement> {
  id: string;
  imageNumber: number;
  imageSrc?: string;
  imageAlt?: string;
  selectedEffect?: string;
  onEffectChange?: (effect: string) => void;
  onFileUpload?: (files: File[]) => void;
  onRemove?: () => void;
  disabled?: boolean;
  isDraggable?: boolean;
  isUploading?: boolean;
  mediaType?: "image" | "video";
  format?: "16:9" | "9:16";
}

function useEffects() {
  const { t } = useTranslation();
  return [
    { value: "zoom-in", label: t("imageEffectCard.effects.zoomIn"), beta: false },
    { value: "zoom-out", label: t("imageEffectCard.effects.zoomOut"), beta: false },
    { value: "pan-left", label: t("imageEffectCard.effects.panLeft"), beta: true },
    { value: "pan-right", label: t("imageEffectCard.effects.panRight"), beta: true },
  ];
}

const ACCEPTED_MEDIA_TYPES = [
  "image/jpeg", "image/png", "image/webp", "image/gif",
  "video/mp4", "video/quicktime", "video/webm"
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB (matches useImageUpload hook limit)

const ImageEffectCard = ({
  id,
  imageNumber,
  imageSrc,
  imageAlt = "Image preview",
  selectedEffect = "zoom-in",
  onEffectChange,
  onFileUpload,
  onRemove,
  disabled = false,
  isDraggable = true,
  isUploading: isUploadingProp = false,
  mediaType = "image",
  format = "16:9",
  className,
  ...props
}: ImageEffectCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0, rotateZ: 0 });
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLocalUploading, setIsLocalUploading] = useState(false);
  const isUploading = isUploadingProp || isLocalUploading;
  const { t } = useTranslation();
  const effects = useEffects();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isDraggable || disabled });

  // Track mouse movement for Balatro-style gravity effect
  useEffect(() => {
    if (!isDragging) {
      setTilt({ rotateX: 0, rotateY: 0, rotateZ: 0 });
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!cardRef.current) return;

      const rect = cardRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Calculate distance from center (normalized to -1 to 1)
      const deltaX = (e.clientX - centerX) / (rect.width / 2);
      const deltaY = (e.clientY - centerY) / (rect.height / 2);

      // Apply tilt (rotateY for horizontal, rotateX for vertical - inverted)
      const maxTilt = -4; // Maximum tilt in degrees
      const maxZTilt = -8; // Maximum Z rotation in degrees
      setTilt({
        rotateX: -deltaY * maxTilt,
        rotateY: deltaX * maxTilt,
        rotateZ: deltaX * maxZTilt,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [isDragging]);

  const validateFiles = useCallback((files: FileList | File[]): File[] => {
    const validFiles: File[] = [];
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      if (!ACCEPTED_MEDIA_TYPES.includes(file.type)) {
        console.warn(`Invalid file type: ${file.type}`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        console.warn(`File too large: ${file.name}`);
        continue;
      }
      validFiles.push(file);
    }

    return validFiles;
  }, []);

  const handleFileSelect = useCallback(
    async (files: FileList | File[]) => {
      if (disabled || !onFileUpload) return;

      const validFiles = validateFiles(files);
      if (validFiles.length === 0) return;

      setIsLocalUploading(true);
      try {
        await onFileUpload(validFiles);
      } finally {
        setIsLocalUploading(false);
      }
    },
    [disabled, onFileUpload, validateFiles]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelect(files);
      }
    },
    [disabled, handleFileSelect]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files);
      }
      // Reset input so the same file can be selected again
      e.target.value = "";
    },
    [handleFileSelect]
  );

  const handleUploadClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (disabled) return;

      const items = e.clipboardData.items;
      const files: File[] = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        handleFileSelect(files);
      }
    },
    [disabled, handleFileSelect]
  );

  // Combine dnd-kit transform with our tilt effect
  const baseTransform = CSS.Transform.toString(transform);
  const tiltTransform = isDragging
    ? `perspective(1000px) rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) rotateZ(${tilt.rotateZ}deg) scale(1.02)`
    : "";

  const style = {
    transform: baseTransform ? `${baseTransform} ${tiltTransform}` : tiltTransform,
    transition: isDragging ? "transform 0.1s ease-out" : transition,
    opacity: 1,
    zIndex: isDragging ? 1000 : 1,
    position: "relative" as const,
    transformStyle: "preserve-3d" as const,
  };

  return (
    <Card
      ref={(node) => {
        setNodeRef(node);
        (cardRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      style={style}
      className={cn(
        "bg-white rounded-3xl border border-black/5 shadow-none transition-all",
        "2k:rounded-[40px] 4k:rounded-[60px]",
        isDragging && "shadow-2xl z-50",
        className
      )}
      onPaste={handlePaste}
      {...props}
    >
      <CardContent className="p-0">
        {/* Top Handle Section */}
        <div 
          {...(isDraggable && !disabled ? { ...attributes, ...listeners } : {})}
          className={cn(
            "w-full h-6 2k:h-12 4k:h-16 flex items-center justify-center pt-2 rounded-t-3xl",
            isDraggable && !disabled ? "cursor-grab active:cursor-grabbing" : "cursor-default opacity-30"
          )}
        >
          <div className="w-12 h-1 2k:w-20 2k:h-2 4k:w-32 4k:h-3 bg-black/10 rounded-full" />
        </div>

        <div className="p-4 2k:p-8 4k:p-12 space-y-4 2k:space-y-8 4k:space-y-12">
          {/* Header Info */}
          <div className="flex items-center justify-between">
            <span className="bg-black/5 text-black px-3 py-1 2k:px-6 2k:py-2 rounded-full text-[10px] 2k:text-lg 4k:text-2xl font-bold uppercase tracking-widest">
              {mediaType === "video" 
                ? t("imageEffectCard.videoLabel", { number: imageNumber }) 
                : t("imageEffectCard.imageLabel", { number: imageNumber })}
            </span>
          </div>

          <div className="flex flex-col space-y-4 2k:space-y-8 4k:space-y-12">
            {/* Image Upload Area */}
            <div className="flex-1 min-w-0">
              <div
                className={cn(
                  "w-full rounded-2xl 2k:rounded-[32px] 4k:rounded-[48px] overflow-hidden relative group border border-black/5",
                  "aspect-video",
                  "cursor-pointer",
                  !imageSrc && "border-dashed border-black/10 hover:border-black/20",
                  isDragOver && "ring-4 ring-black ring-offset-4",
                  disabled && "opacity-40 grayscale"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleUploadClick}
              >
                {imageSrc ? (
                  <>
                    {mediaType === "video" ? (
                      <video
                        src={imageSrc}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        muted
                        loop
                        onMouseOver={(e) => e.currentTarget.play().catch(() => {})}
                        onMouseOut={(e) => e.currentTarget.pause()}
                      />
                    ) : (
                      <img
                        src={imageSrc}
                        alt={imageAlt}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all pointer-events-none" />
                    
                    {isUploading && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
                        <Loader2 className="w-8 h-8 2k:size-16 4k:size-24 animate-spin text-white" />
                      </div>
                    )}
                    
                    {onRemove && !disabled && !isUploading && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove();
                        }}
                        className="absolute top-4 right-4 p-2 bg-white/90 hover:bg-white text-black rounded-full shadow-lg transition-all scale-0 group-hover:scale-100 cursor-pointer"
                        aria-label={t("imageEffectCard.removeImage")}
                      >
                        <X className="size-4 2k:size-8 4k:size-12" />
                      </button>
                    )}
                  </>
                ) : (
                  <div
                    className={cn(
                      "flex flex-col w-full h-full bg-black/[0.02] items-center justify-center transition-all",
                      isDragOver ? "bg-black/5" : "hover:bg-black/[0.04]",
                      format === "9:16" ? "aspect-[9/16]" : "aspect-video"
                    )}
                  >
                    {isUploading ? (
                      <Loader2 className="w-12 h-12 2k:size-24 4k:size-36 animate-spin text-black" />
                    ) : (
                      <>
                        <div className="w-16 h-16 2k:size-28 4k:size-40 rounded-full bg-black/5 flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                          <Upload className="w-8 h-8 2k:size-14 4k:size-20 text-black" />
                        </div>
                        <p className="text-sm 2k:text-2xl 4k:text-4xl font-bold text-black mb-1">
                          {isDragOver ? t("imageEffectCard.dropToUpload") : t("imageEffectCard.upload")}
                        </p>
                        <p className="text-[12px] 2k:text-lg 4k:text-2xl text-muted-foreground tracking-widest text-center px-4">
                          {t("imageEffectCard.dragOrPaste")}
                        </p>
                      </>
                    )}
                  </div>
                )}
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple={!imageSrc}
                  accept={ACCEPTED_MEDIA_TYPES.join(",")}
                  onChange={handleInputChange}
                  className="hidden"
                  disabled={disabled}
                />
              </div>
            </div>

            {/* Effect Selector Chips */}
            <div className="space-y-3 2k:space-y-6 w-full">
              <Label className="text-[10px] 2k:text-lg 4k:text-2xl font-bold text-muted-foreground uppercase tracking-wider">
                {t("imageEffectCard.effectLabel")}
              </Label>
              <div className="flex gap-2 2k:gap-4 4k:gap-6 flex-wrap">
                {effects.map((effect) => {
                  const isActive = selectedEffect === effect.value;
                  return (
                    <button
                      key={effect.value}
                      type="button"
                      disabled={disabled || !imageSrc || isUploading || mediaType === "video"}
                      onClick={() => onEffectChange?.(effect.value)}
                      className={cn(
                        "px-4 py-2 2k:px-8 2k:py-4 rounded-full text-xs 2k:text-2xl 4k:text-4xl font-bold transition-all cursor-pointer",
                        "relative border whitespace-nowrap w-fit",
                        isActive
                          ? "bg-black border-black text-white shadow-lg"
                          : "bg-transparent border-black/10 text-black/60 hover:border-black/30 hover:text-black",
                        (disabled || !imageSrc || isUploading || mediaType === "video") && "opacity-20 cursor-default"
                      )}
                    >
                      <div className="flex items-center gap-2 justify-center">
                        {effect.label}
                        {effect.beta && (
                          <div className="relative group/beta flex items-center ml-1">
                            <span className={cn(
                              "text-[9px] 2k:text-base px-1.5 py-0.5 rounded-md font-bold uppercase tracking-wide leading-none",
                              isActive 
                                ? "bg-white/20 text-white border border-white/20" 
                                : "bg-black/5 text-black/40 border border-black/5"
                            )}>
                              Beta
                            </span>
                            {/* Beta Tooltip */}
                            <div className="absolute bottom-full mb-3 px-4 py-3 w-64 max-w-[80vw] bg-gray-900 text-white text-sm leading-relaxed rounded-xl hidden group-hover/beta:block z-[100] shadow-2xl pointer-events-none normal-case font-medium text-center whitespace-normal left-1/2 -translate-x-1/2">
                              {t("imageEffectCard.betaTooltip")}
                              <div className="absolute top-full border-8 border-transparent border-t-gray-900 left-1/2 -translate-x-1/2" />
                            </div>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export { ImageEffectCard, useEffects as effects };
